import _ from "lodash";
import React, { useState, useMemo, useRef, useEffect } from "react";
import { graphql, compose } from "lib/apolloClient";
import classes from "./AddClassModal.scss";
import GroupedList from "UIComponents/GroupedList";
import I18nHOC from "UIComponents/I18nHOC";
import { getStaffCoursesV2BasicDetailsQuery } from "Teacher/modules/TeacherQuery";
import { getStaffCoursesV2BasicDetailsFromCache } from "Teacher/modules/TeacherGraphqlHelpers";
import { getOrganizationGradesQuery } from "CommonGraphql/Organization/OrganizationQueries";
import { connect } from "react-redux";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getMatchingIdsDataMemoize,
  getSuperMinusSubMemoize,
} from "modules/Services";
import {
  Button,
  IconButton,
  Avatar,
  ChecklistDropdown,
  FilterLabelButton,
} from "@toddle-edu/ds-web";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import { AVATAR_COLORS } from "Constants/colorConstants";
import { CURRICULUM_TYPE_PYP, VISIBLE } from "Constants/stringConstants";
import {
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
  getUserType,
} from "modules/CommonSelectors";
import {
  getAssignmentConsumingParentModulesPermission,
  getFilteredCoursesBasedOnViewOrManagePermission,
  getStaffCourseDetailsQueryVariables,
} from "ClassRoom/modules/utils";
import { getIsAddClassPaginationEnabled } from "Utils/ACLStoreUtils";
import {
  ASSIGNMENTS_MANAGE_PERMISSION_IN_CALENDAR,
  PSOW_ROEP_MANAGE_PERMISSION,
} from "Constants/permissionConstants";
import classNames from "classnames";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { getLearningCourseDetailsQuery } from "ClassRoom/modules/ClassRoomQuery";
import {
  getLabelValueForMultiFilterLabelButton,
  prepareLoadMoreFeed,
  useIntersectionObserver,
} from "Utils";
import { getSubjectsWithNoGradebook } from "GradingSetup/modules/Utils";
import MoreItemWithToolTipV2 from "UIComponents/MoreItemWithToolTipV2/MoreItemWithToolTipV2";
import { isToddleLibraryRouteBySegment as isToddleLibraryRoute } from "Utils/RouteMatchUtils";
import { isRnpConsumptionEnabled } from "Teacher/routes/ToddleAnalytics/modules/Utils";

const GradeFilter = React.memo(
  ({ options, value, onChange, labelValue, testId, t }) => {
    if (options.length === 0) return null;
    return (
      <ChecklistDropdown
        options={options}
        value={value}
        onChange={onChange}
        showAllOption={true}
        allOptionLabel={t("common:all")}
        checklistLabel={t("common:grade")}
        testId={`${testId}-gradeFilter`}
      >
        <FilterLabelButton
          testId={`${testId}-gradeFilter`}
          type="outlined"
          variant="primary"
          label={t("common:grade")}
          labelValue={labelValue}
          dsVersion="2.0"
        />
      </ChecklistDropdown>
    );
  }
);
GradeFilter.displayName = "GradeFilter";

const getClassesList = (
  t,
  classesFromQuery,
  allGrades,
  filteredStaffCoursesIds,
  showGroupedList
) => {
  const isToddleLibrary = isToddleLibraryRoute();

  if (!showGroupedList) {
    const filteredClasses = _.filter(classesFromQuery, course => {
      const isArchived = _.get(course, "node.isArchived", false);
      return !isArchived;
    });

    if (_.isEmpty(filteredClasses)) {
      return [];
    }

    return [
      {
        id: "ALL",
        label: t("common:all_classes_plural"),
        options: _.map(filteredClasses, course => {
          const { title, profileImage, students, id } = _.get(
            course,
            "node",
            {}
          );
          const grades = _.get(course, "node.grades", []);

          const subtitle = _.size(grades) ? (
            <MoreItemWithToolTipV2
              items={_.map(grades, "name")}
              length={1}
              customClass="text-body-s"
              moreItemCustomClass="text-body-s"
            />
          ) : null;

          return {
            id,
            title,
            subtitle,
            value: id,
            profileImage,
            students,
            icon: (
              <Avatar
                key={id}
                size={"medium"}
                shade={"light"}
                iconType={"group"}
                color={AVATAR_COLORS[id % _.size(AVATAR_COLORS)]}
                src={profileImage}
              />
            ),
            disabled:
              !isToddleLibrary &&
              getIsLearningCoursesEnabled() &&
              _.isArray(filteredStaffCoursesIds) &&
              !_.includes(filteredStaffCoursesIds, id),
            disabledTooltip: t(
              "classRoom:no_access_to_add_class_to_assignment"
            ),
          };
        }),
      },
    ];
  }

  return _.reduce(
    allGrades,
    (result, grade) => {
      const gradeCourses = _.filter(classesFromQuery, course => {
        const grades = _.get(course, "node.grades", []);
        const isCourseIncluded = _.findIndex(grades, { id: grade?.id }) >= 0;

        return isCourseIncluded;
      });

      const filteredGradeCourses = _.filter(gradeCourses, course => {
        const isArchived = _.get(course, "node.isArchived", false);
        return !isArchived;
      });

      if (_.size(filteredGradeCourses) > 0) {
        const { name, ...rest } = grade;
        result.push({
          ...rest,
          label: name,
          options: _.map(filteredGradeCourses, course => {
            const { title, profileImage, students, id } = _.get(
              course,
              "node",
              {}
            );
            return {
              id,
              title,
              value: id,
              profileImage,
              students,
              icon: (
                <Avatar
                  key={id}
                  size={"x-small"}
                  shade={"light"}
                  iconType={"group"}
                  color={AVATAR_COLORS[id % _.size(AVATAR_COLORS)]}
                  src={profileImage}
                />
              ),
            };
          }),
        });
      }

      return result;
    },
    []
  );
};

const getFilteredClasses = ({
  classesFromQuery,
  curriculumType,
  isQt,
  isDpPa,
  isDpEp,
}) => {
  // As CAS and EE does not have planning so we can only create quick task in them
  if (curriculumType === "IB_DP" && !isQt) {
    return _.filter(classesFromQuery, ({ node }) => {
      const subjectType = _.get(node, "subjects.0.type", "");
      return !_.includes(getSubjectsWithNoGradebook(), subjectType);
    });
  } else if (isDpPa) {
    return _.filter(classesFromQuery, ({ node }) => {
      const subjects = _.get(node, "subjects", []);
      const isPsow = _.get(subjects, "[0].metadata.isPsow", false);
      const isESAS = _.get(subjects, "[0].metadata.isESAS", false);
      return isPsow || isESAS;
    });
  } else if (isDpEp) {
    return _.filter(classesFromQuery, ({ node }) => {
      const subjects = _.get(node, "subjects", []);
      const isRoep = _.get(subjects, "[0].metadata.isRoep", false);
      return isRoep;
    });
  }
  return classesFromQuery;
};

const AddClassModal = React.memo(
  ({
    classesFromQuery,
    isData,
    isLoading,
    onClickAddClasses,
    onClickClose,
    coursesSearchText,
    changeCoursesSeachText,
    selectedTimeSlot,
    t,
    allGrades,
    scoreCategoryLabel,
    subjectName,
    curriculumType,
    isQt,
    isDpPa,
    isDpEp,
    testId = "classRoom-addAssignment",
    filteredStaffCoursesIds,
    isLearningCoursesEnabled,
    isPaginationEnabled = false,
    hasNextPage,
    loadMoreFeed,
    isFetchingMoreData,
    selectedGradeIds,
    onGradeFilterChange,
    subjectsField,
    curriculumGrades,
  }) => {
    const [selectedClasses, setSelectedClasses] = useState([]);
    const loaderRef = useRef(null);
    const isLoaderVisible = useIntersectionObserver(loaderRef, {
      threshold: 0.7,
    });

    const updateSelectedItems = items => {
      const updatedItems = _.map(items, item => {
        const totalStudents = _.get(item, "students", []);
        const state = {
          state: "PUBLISHED",
          scheduleDate: _.get(selectedTimeSlot, "startDate", null),
          scheduleTime: _.get(selectedTimeSlot, "startTime", null),
        };
        return {
          ..._.omit(item, "icon"),
          totalStudents,
          state,
          visibility: VISIBLE,
        };
      });
      setSelectedClasses(updatedItems);
    };

    // Load more data when loader is visible
    useEffect(() => {
      if (isLoaderVisible && hasNextPage && !isFetchingMoreData) {
        loadMoreFeed();
      }
    }, [isLoaderVisible, hasNextPage, isFetchingMoreData, loadMoreFeed]);

    // case 1 : classesFromQueryIsEmpty ? hideSearchInput + hideFooter
    // case 2 : searchTextIsNotEmpty and classesFromQueryisEmpty ? hideFooter
    // (it means that user has searched something but got no result back)
    // case 3 : searchTextIsEmpty and classesFromQueryIsNotEmpty ? showSearchInput + showFooter
    const showEmptyState =
      _.isEmpty(classesFromQuery) &&
      (isPaginationEnabled ? !hasNextPage : true);
    const showSearchInput =
      !showEmptyState ||
      !_.isEmpty(coursesSearchText) ||
      !_.isEmpty(selectedGradeIds);
    const showGradeFilter = !showEmptyState || !_.isEmpty(selectedGradeIds);

    const filteredClasses = useMemo(
      () =>
        getFilteredClasses({
          classesFromQuery,
          curriculumType,
          isQt,
          isDpPa,
          isDpEp,
        }),
      [classesFromQuery, curriculumType, isQt, isDpPa, isDpEp]
    );

    const selectedCountClass = classNames(
      "text-label-3",
      classes.selectedCount,
      {
        [classes.selectedCountDisabled]: _.isEmpty(selectedClasses),
      }
    );

    const showGroupedList = !isLearningCoursesEnabled && !isPaginationEnabled;

    //  - PYP: all grades of the curriculum program (via getOrganizationGrades)
    //  - Other curriculums: union of grades across subjectsField's subjects
    const gradesForFilter = useMemo(() => {
      if (curriculumType === CURRICULUM_TYPE_PYP) {
        return curriculumGrades || [];
      }
      const subjects = _.get(subjectsField, "resolvedMinimalTree.subjects", []);
      return _.uniqBy(
        _.flatMap(subjects, subject => _.get(subject, "grades", [])),
        "id"
      );
    }, [curriculumType, curriculumGrades, subjectsField]);

    const gradeFilterOptions = useMemo(
      () =>
        _.map(gradesForFilter, grade => ({
          value: grade.id,
          label: grade.name,
        })),
      [gradesForFilter]
    );

    const gradeFilterLabelValue = useMemo(() => {
      const selectedLabels = _.map(
        _.filter(gradeFilterOptions, opt =>
          _.includes(selectedGradeIds, opt.value)
        ),
        opt => opt.label
      );
      return getLabelValueForMultiFilterLabelButton({
        options: gradeFilterOptions,
        selectedOptions: selectedLabels,
        t,
        noneText: t("common:none"),
        allText: t("common:all"),
      });
    }, [gradeFilterOptions, selectedGradeIds, t]);

    return (
      <div className={classes.container}>
        <div className={classes.headerCon}>
          <div className={classes.addClassesLabel}>
            {t("common:add_with_label", { label: t("common:class") })}
          </div>

          <IconButton
            icon={<CloseOutlined />}
            _legacy_variant={"plain"}
            variant={"neutral"}
            type={"plain"}
            onClick={onClickClose}
            testId={`${testId}-close`}
          />
        </div>
        <div className={classes.modalBody}>
          <GroupedList
            testId={testId}
            items={getClassesList(
              t,
              filteredClasses,
              allGrades,
              filteredStaffCoursesIds,
              showGroupedList
            )}
            additionalFilter={
              showGradeFilter ? (
                <GradeFilter
                  options={gradeFilterOptions}
                  value={selectedGradeIds}
                  onChange={onGradeFilterChange}
                  labelValue={gradeFilterLabelValue}
                  testId={testId}
                  t={t}
                />
              ) : null
            }
            showSearchInput={showSearchInput}
            searchValue={coursesSearchText}
            onChangeSearchValue={changeCoursesSeachText}
            selectedItems={selectedClasses}
            setSelectedItems={updateSelectedItems}
            isData={isData}
            isLoading={isLoading}
            showEmptyState={showEmptyState}
            emptyItemsTitle={t("classRoom:yet_to_add_a_class")}
            emptyItemsSubtitle={t("classRoom:no_classes_to_select")}
            emptySearchTitle={t("common:noResultHeading")}
            emptySearchSubtitle={t("common:no_items_matching_your_search")}
            category={scoreCategoryLabel}
            subject={subjectName}
            loaderRef={loaderRef}
            hasNextPage={hasNextPage}
            showGroupedList={showGroupedList}
          />
        </div>
        {!showEmptyState ? (
          <div className={classes.footerCon}>
            <div className={selectedCountClass}>
              {t("common:count_selected", { count: _.size(selectedClasses) })}
            </div>
            <Button
              size="medium"
              onClick={() => onClickAddClasses(selectedClasses)}
              variant="progressive"
              type="fill"
              testId={`${testId}-add`}
              dsVersion={"2.0"}
              shortcut={{ code: "Enter", shiftKey: true }}
            >
              {t("common:add")}
            </Button>
          </div>
        ) : null}
      </div>
    );
  }
);

AddClassModal.displayName = "AddClassModal";

const EMPTY_OBJECT = {};

const mapStateToProps = (state, ownProps) => {
  const userInfo =
    getUserInfo({ portalType: ownProps.portalType }) || EMPTY_OBJECT;
  const organizationId = _.get(userInfo, "org_id");
  const academicYearId = getSelectedAcademicYearId(state);
  const isToddleLibrary = state?.login?.activeTab == "TODDLE_LIBRARY";

  const {
    subjectsField = EMPTY_OBJECT,
    curriculumProgram = EMPTY_OBJECT,
    isSourceMeetingCreation = false,
    isDpPa,
    isDpEp,
  } = ownProps;

  const currentCurriculumProgramId = isToddleLibrary
    ? curriculumProgram?.id
    : getSelectedCurriculumId(state);

  const curriculumType = isToddleLibrary
    ? curriculumProgram?.type
    : getCurrentCurriculumProgramType(state);

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  const isPaginationEnabled = getIsAddClassPaginationEnabled();

  let controlParameters;
  if (isRnpConsumptionEnabled()) {
    controlParameters = [];

    if (isSourceMeetingCreation) {
      controlParameters.push(ASSIGNMENTS_MANAGE_PERMISSION_IN_CALENDAR);
    } else {
      const permissions =
        isDpPa || isDpEp
          ? [PSOW_ROEP_MANAGE_PERMISSION]
          : getAssignmentConsumingParentModulesPermission("MANAGE");

      controlParameters.push(...permissions);
    }
  }

  return {
    userId: userInfo?.id,
    userType: getUserType(state),
    academicYearId,
    curriculumType,
    currentCurriculumProgramId,
    organizationId,
    subjectsField,
    isPaginationEnabled,
    isLearningCoursesEnabled,
    isSourceMeetingCreation,
    controlParameters,
  };
};

const getStudents = classItem => {
  const studentsEdges = _.get(classItem, "allStudents.edges", []);
  return _.map(studentsEdges, ({ node }) => node);
};

const getRestructuredData = ({ classes }) => {
  return _.map(classes, classItem => {
    return {
      node: {
        ...classItem,
        students: getStudents(classItem),
      },
    };
  });
};
export const getRestructuredDataMemoize = _.memoize(
  params => getRestructuredData(params),
  params => JSON.stringify(params)
);

const getAllGrades = _.memoize(classes => {
  const allGrades = _.reduce(
    classes,
    (result, classItem) => {
      const grades = _.get(classItem, "grades", []);
      result.push(...grades);
      return result;
    },
    []
  );
  return _.uniqBy(allGrades, "id");
}, JSON.stringify);

export default compose(
  I18nHOC({ resource: ["common", "classRoom"] }),
  connect(mapStateToProps, null),
  graphql(getOrganizationGradesQuery, {
    name: "getOrganizationGrades",
    skip: ({
      curriculumType,
      isLearningCoursesEnabled,
      organizationId,
      currentCurriculumProgramId,
    }) =>
      curriculumType !== CURRICULUM_TYPE_PYP ||
      isLearningCoursesEnabled ||
      !organizationId ||
      !currentCurriculumProgramId,
    options: ({ organizationId, currentCurriculumProgramId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: organizationId,
        filters: {
          curriculumProgramIds: [currentCurriculumProgramId],
        },
      },
    }),
    props({ getOrganizationGrades }) {
      return {
        curriculumGrades: _.get(getOrganizationGrades, "node.grades", []),
      };
    },
  }),
  graphql(getStaffCoursesV2BasicDetailsQuery, {
    name: "getStaffCoursesBasicDetails",
    skip: ({ isLearningCoursesEnabled, learningCourseId }) =>
      isLearningCoursesEnabled && learningCourseId,
    options: ({
      userId,
      portalType,
      coursesSearchText,
      courseId,
      subjectsField,
      grades,
      isDpPa,
      isDpEp,
      academicYearId,
      userType,
      curriculumType,
      currentCurriculumProgramId,
      isPaginationEnabled,
      learningCourseId,
      isSourceMeetingCreation,
      controlParameters,
      selectedGradeIds,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: getStaffCourseDetailsQueryVariables({
        userId,
        portalType,
        coursesSearchText,
        courseId,
        subjectsField,
        grades,
        isDpPa,
        isDpEp,
        academicYearId,
        isPaginationEnabled,
        userType,
        curriculumType,
        currentCurriculumProgramId,
        learningCourseId,
        isSourceMeetingCreation,
        controlParameters,
        selectedGradeIds,
      }),
    }),
    props({
      getStaffCoursesBasicDetails,
      ownProps: {
        courses,
        courseIdsOfScoreCategorySet,
        isSourceMeetingCreation,
      },
    }) {
      const queryData = getStaffCoursesV2BasicDetailsFromCache(
        getStaffCoursesBasicDetails.variables
      );

      const allClassEdges = _.get(queryData, "coursesV2.edges", []);
      let allClasses = _.filter(
        _.map(allClassEdges, edge => _.get(edge, "node", {})),
        edge => !_.isEmpty(edge)
      );
      allClasses = isSourceMeetingCreation
        ? allClasses
        : getFilteredCoursesBasedOnViewOrManagePermission({
            courses: allClasses,
            permissionsToCheck: [], // * Not required as RnP permissions handled through BE
          });

      const allGrades = getAllGrades(allClasses);

      let classesToDisplay = [];
      let gradesToConsider = [];
      if (!_.isEmpty(allClasses)) {
        // courses array is a state, and will contain selected classes details
        const selectedClasses = courses;

        gradesToConsider = !_.isEmpty(courseIdsOfScoreCategorySet)
          ? getMatchingIdsDataMemoize({
              arrayOfObject: allGrades,
              idsArray: courseIdsOfScoreCategorySet,
            })
          : allGrades;

        // get unselected classes
        const unselectedClasses = getSuperMinusSubMemoize({
          superSet: allClasses,
          subSet: selectedClasses,
        });

        // restructure data
        classesToDisplay = getRestructuredDataMemoize({
          classes: unselectedClasses,
        });
      }

      const hasNextPage = _.get(
        queryData,
        "coursesV2.pageInfo.hasNextPage",
        false
      );
      const endCursor = _.get(queryData, "coursesV2.pageInfo.endCursor", null);

      return {
        networkStatus: getStaffCoursesBasicDetails.networkStatus,
        isData: !_.isEmpty(queryData),
        classesFromQuery: classesToDisplay,
        allGrades: gradesToConsider,
        isLoading:
          getStaffCoursesBasicDetails["networkStatus"] == 1 ||
          getStaffCoursesBasicDetails["networkStatus"] == 2,
        hasNextPage,
        isFetchingMoreData: getStaffCoursesBasicDetails["networkStatus"] == 3,
        loadMoreFeed: prepareLoadMoreFeed({
          query: getStaffCoursesV2BasicDetailsQuery,
          variables: {
            ...getStaffCoursesBasicDetails.variables,
            after: endCursor,
          },
          queryData: getStaffCoursesBasicDetails,
          key: "coursesV2",
        }),
      };
    },
  }),
  /**
   * In new course setup show all classes of learning course which are not selected
   */
  graphql(getLearningCourseDetailsQuery, {
    skip: ({ learningCourseId, isLearningCoursesEnabled }) =>
      !learningCourseId || !isLearningCoursesEnabled,
    name: "getLearningCourseDetails",
    options: ({ learningCourseId, coursesSearchText, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        learningCourseId,
        learningCourseClassFilter: {
          searchText: coursesSearchText,
        },
        portalType,
      },
    }),
    props({
      getLearningCourseDetails,
      ownProps: { isData, isLoading, courses },
    }) {
      const learningCourseDetails = _.get(getLearningCourseDetails, "node", {});
      const learningCourseClasses = _.get(learningCourseDetails, "classes", []);

      // get unselected classes
      const unselectedClasses = getSuperMinusSubMemoize({
        superSet: learningCourseClasses,
        subSet: courses,
      });

      // restructure data
      const classesToDisplay = getRestructuredDataMemoize({
        classes: unselectedClasses,
      });

      return {
        classesFromQuery: classesToDisplay,
        isData: !_.isEmpty(learningCourseDetails) && isData,
        isLoading:
          _.includes([1, 2], getLearningCourseDetails?.networkStatus) ||
          isLoading,
      };
    },
  })
)(AddClassModal);
